/**
 * Mobile Filter Chips Component
 * 
 * Displays active filters as removable chips above the store list.
 * 
 * @module MobileFilterChips
 */

import React from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius, Transitions } from '../../styles/ThemeStyles';
import { MobileFilterOptions } from './MobileFilterSheet';

interface MobileFilterChipsProps {
  filters: MobileFilterOptions;
  onRemoveFilter: (key: keyof MobileFilterOptions) => void;
  onClearAll: () => void;
  availableProvinces: string[];
  availableCities: string[];
  availableSalesGroups?: string[];
  availableSortFields?: string[];
}

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.md};
`;

const FilterChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: ${ThemeColors.primary.light};
  color: ${ThemeColors.primary.main};
  border-radius: ${BorderRadius.full};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
`;

const ChipLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const ChipRemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  background: transparent;
  color: ${ThemeColors.primary.main};
  cursor: pointer;
  border-radius: ${BorderRadius.full};
  padding: 0;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:active {
    background-color: ${ThemeColors.primary.main};
    color: white;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ClearAllButton = styled.button`
  padding: ${Spacing.xs} ${Spacing.sm};
  background-color: transparent;
  color: ${ThemeColors.text.secondary};
  border: 1px solid ${ThemeColors.border.light};
  border-radius: ${BorderRadius.full};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${Transitions.duration.short} ${Transitions.easing.easeInOut};

  &:active {
    background-color: ${ThemeColors.gray[100]};
    border-color: ${ThemeColors.border.main};
  }
`;

export const MobileFilterChips: React.FC<MobileFilterChipsProps> = ({
  filters,
  onRemoveFilter,
  onClearAll,
  availableProvinces,
  availableCities,
  availableSalesGroups = [],
  availableSortFields = []
}) => {
  const activeFilters = Object.entries(filters).filter(([_, value]) => value && value !== '');

  if (activeFilters.length === 0) {
    return null;
  }

  const getFilterLabel = (key: string, value: string): string => {
    switch (key) {
      case 'province':
        return value;
      case 'city':
        return value;
      case 'salesGroup':
        return value;
      case 'sortField':
        return `Sort: ${value}`;
      case 'status':
        return value;
      default:
        return value;
    }
  };

  return (
    <ChipsContainer>
      {activeFilters.map(([key, value]) => (
        <FilterChip key={key}>
          <ChipLabel>
            {key === 'province' && '📍'}
            {key === 'city' && '🏙️'}
            {key === 'salesGroup' && '👥'}
            {key === 'sortField' && '🔢'}
            {key === 'status' && '📊'}
            {getFilterLabel(key, value as string)}
          </ChipLabel>
          <ChipRemoveButton
            onClick={() => onRemoveFilter(key as keyof MobileFilterOptions)}
            aria-label="Remove filter"
          >
            <X size={14} />
          </ChipRemoveButton>
        </FilterChip>
      ))}
      {activeFilters.length > 1 && (
        <ClearAllButton onClick={onClearAll}>
          Clear all
        </ClearAllButton>
      )}
    </ChipsContainer>
  );
};

